import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "adyen-modal__header" }
const _hoisted_2 = { class: "adyen-modal__header-title" }
const _hoisted_3 = { class: "typographies-paragraph-medium-regular adyen-modal__description" }
const _hoisted_4 = { class: "adyen-modal__header-close" }
const _hoisted_5 = { class: "adyen-modal__body" }
const _hoisted_6 = { class: "adyen-modal__section" }
const _hoisted_7 = { class: "typographies-paragraph-large-semibold adyen-modal__section-title" }
const _hoisted_8 = { class: "typographies-paragraph-medium-regular adyen-modal__section-description" }
const _hoisted_9 = { class: "typographies-heading-h4 adyen-modal__help-link" }

import { ref, computed, onMounted, inject } from 'vue';
import type { Translator } from 'types/injects/translator.type';
import type { PushToast } from 'types/injects/pushToast.type';
import { getSettings, saveSettings } from 'api/plugins/adyenService';
import Payments from 'api/payments.js';
import AdyenModalLoader from 'vcomponents/settings/adyen/AdyenModalLoader.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'AdyenModal',
  emits: ["close"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const translations: Record<string, string> = {
    connectDescription: $t.__('settings.adyen.AdyenPopup.connect_description'),
    unavailableError: $t.__('settings.adyen.AdyenPopup.unavailable_error'),
    installationTitle: $t.__('settings.adyen.AdyenPopup.installation_title'),
    installationDescription: $t.__('settings.adyen.AdyenPopup.installation_description'),
    viewDocumentation: $t.__('settings.adyen.AdyenPopup.view_documentation'),
    needHelp: $t.__('common.labels.need_help'),
    apiKeyLabel: $t.__('settings.adyen.AdyenPopup.api_key_label'),
    hmacKeyLabel: $t.__('settings.adyen.AdyenPopup.hmac_key_label'),
    environmentLabel: $t.__('settings.adyen.AdyenPopup.environment_label'),
    apiUrlPrefixLabel: $t.__('settings.adyen.AdyenPopup.api_url_prefix_label'),
    merchantAccountLabel: $t.__('settings.adyen.AdyenPopup.merchant_account_label'),
    themeIdLabel: $t.__('settings.adyen.AdyenPopup.theme_id_label'),
    paymentMethodLabel: $t.__('settings.adyen.AdyenPopup.payment_method_label'),
    paymentMethodInfo: $t.__('settings.adyen.AdyenPopup.payment_method_info'),
    cancel: $t.__('common.actions.cancel'),
    save: $t.__('common.actions.save'),
    fieldRequired: $t.__('common.labels.this_field_is_obligatory'),
    settingsSaved: $t.__('common.labels.settings_saved'),
    toastError: $t.__('common.labels.toast.error'),
};

const showModal = ref(true);
const loading = ref(true);
const isFormSubmitted = ref(false);
const adyenForm = ref<Record<string, unknown>>({});
const errorOnAdyen = ref(false);
const shouldUpdateSettings = ref(true);
const formErrors = ref<Record<string, string>>({});
const meansOfPaymentOptions = ref<{ id: number; label: string }[] | null>(null);

const envOptions: { value: boolean; label: string }[] = [
    { value: false, label: 'Production' },
    { value: true, label: 'Test' },
];

const primaryAction = computed((): { label: string; loading: boolean; action: () => void } => ({
    label: translations.save,
    loading: isFormSubmitted.value,
    action: () => submitForm(),
}));

const cancelButtonProps = {
    label: translations.cancel,
};

const requiredFields = ['api_key', 'notification_hmac_key', 'merchant_account'] as const;

const validate = (): boolean => {
    formErrors.value = {};
    let isValid = true;

    for (const field of requiredFields) {
        const value =
            typeof adyenForm.value[field] === 'string'
                ? (adyenForm.value[field] as string).trim()
                : '';
        if (!value) {
            formErrors.value[field] = translations.fieldRequired;
            isValid = false;
        }
    }

    if (!adyenForm.value.test_env) {
        const liveUrlPrefix =
            typeof adyenForm.value.live_url_prefix === 'string'
                ? (adyenForm.value.live_url_prefix as string).trim()
                : '';
        if (!liveUrlPrefix) {
            formErrors.value.live_url_prefix = translations.fieldRequired;
            isValid = false;
        }
    }

    return isValid;
};

const submitForm = async (): Promise<void> => {
    if (!validate()) {
        return;
    }

    isFormSubmitted.value = true;
    try {
        if (adyenForm.value.test_env) {
            adyenForm.value.live_url_prefix = null;
        }
        await saveSettings(shouldUpdateSettings.value, adyenForm.value);
        $pushToast({
            type: 'success',
            icon: true,
            text: translations.settingsSaved,
        });
        emit('close');
    } catch (error: unknown) {
        const apiError = error as {
            response?: { data?: { error?: { details?: Record<string, string> } } };
        };
        const details = apiError.response?.data?.error?.details;
        if (details) {
            for (const key of Object.keys(details)) {
                if (key === 'more') {
                    errorOnAdyen.value = true;
                } else {
                    formErrors.value = {
                        ...formErrors.value,
                        [key]: details[key],
                    };
                }
            }
        }
        isFormSubmitted.value = false;
    }
};

onMounted(async () => {
    try {
        meansOfPaymentOptions.value = await Payments.getAllPayments();
    } catch {
        $pushToast({
            type: 'error',
            icon: true,
            text: translations.toastError,
        });
        emit('close');
        return;
    }

    try {
        const response = await getSettings();
        adyenForm.value = response.data as Record<string, unknown>;
        if (adyenForm.value.payment_method_id === 0) {
            adyenForm.value.payment_method_id = meansOfPaymentOptions.value?.[0]?.id;
        }
    } catch (error: unknown) {
        const apiError = error as { response?: { data?: { error?: { code?: number } } } };
        if (apiError.response?.data?.error?.code === 404) {
            shouldUpdateSettings.value = false;
            adyenForm.value = {
                test_env: false,
                payment_method_id: meansOfPaymentOptions.value?.[0]?.id,
            };
        } else {
            $pushToast({
                type: 'error',
                icon: true,
                text: translations.toastError,
            });
            emit('close');
        }
    } finally {
        loading.value = false;
    }
});

return (_ctx: any,_cache: any) => {
  const _component_CompassIconButton = _resolveComponent("CompassIconButton")!
  const _component_CompassAlert = _resolveComponent("CompassAlert")!
  const _component_CompassLink = _resolveComponent("CompassLink")!
  const _component_CompassInput = _resolveComponent("CompassInput")!
  const _component_CompassFormItem = _resolveComponent("CompassFormItem")!
  const _component_CompassDropdownOption = _resolveComponent("CompassDropdownOption")!
  const _component_CompassSelect = _resolveComponent("CompassSelect")!
  const _component_CompassModal = _resolveComponent("CompassModal")!

  return (_openBlock(), _createBlock(_component_CompassModal, {
    visible: showModal.value,
    "onUpdate:visible": _cache[12] || (_cache[12] = ($event: any) => ((showModal).value = $event)),
    size: _ctx.CompassEnums.ModalSize.SMALL,
    class: "adyen-modal",
    closeOnClickOverlay: false,
    primaryAction: primaryAction.value,
    cancelButtonProps: cancelButtonProps,
    onClosed: _cache[13] || (_cache[13] = ($event: any) => (emit('close')))
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[14] || (_cache[14] = _createElementVNode("img", {
            class: "adyen-modal__header-icon",
            src: "/ressources/logged/img/illustrations/payment/providerslogo/adyenlogo.svg",
            alt: "Adyen",
            width: "56",
            height: "56"
          }, null, -1 /* CACHED */)),
          _cache[15] || (_cache[15] = _createElementVNode("h2", { class: "typographies-heading-h2 adyen-modal__title" }, "Adyen", -1 /* CACHED */)),
          _createElementVNode("p", _hoisted_3, _toDisplayString(translations.connectDescription), 1 /* TEXT */)
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_CompassIconButton, {
            icon: "close",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('close')))
          })
        ])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_5, [
        (loading.value)
          ? (_openBlock(), _createBlock(AdyenModalLoader, { key: 0 }))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (errorOnAdyen.value)
                ? (_openBlock(), _createBlock(_component_CompassAlert, {
                    key: 0,
                    type: _ctx.CompassEnums.AlertType.ERROR,
                    showClose: false,
                    description: translations.unavailableError
                  }, null, 8 /* PROPS */, ["type", "description"]))
                : _createCommentVNode("v-if", true),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("p", _hoisted_7, _toDisplayString(translations.installationTitle), 1 /* TEXT */),
                _createElementVNode("p", _hoisted_8, _toDisplayString(translations.installationDescription), 1 /* TEXT */),
                _createVNode(_component_CompassLink, {
                  label: translations.viewDocumentation,
                  link: "https://help.sellsy.com/fr/articles/5876306-paiement-en-ligne-des-factures-avec-adyen",
                  target: "_blank"
                }, {
                  prefix: _withCtx(() => [
                    _createElementVNode("span", _hoisted_9, _toDisplayString(translations.needHelp), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["label"])
              ]),
              _createVNode(_component_CompassFormItem, {
                label: translations.apiKeyLabel,
                required: "",
                error: formErrors.value.api_key
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_CompassInput, {
                    modelValue: adyenForm.value.api_key,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((adyenForm.value.api_key) = $event)),
                    error: !!formErrors.value.api_key,
                    onFocus: _cache[2] || (_cache[2] = ($event: any) => (formErrors.value.api_key = ''))
                  }, null, 8 /* PROPS */, ["modelValue", "error"])
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["label", "error"]),
              _createVNode(_component_CompassFormItem, {
                label: translations.hmacKeyLabel,
                required: "",
                error: formErrors.value.notification_hmac_key
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_CompassInput, {
                    modelValue: adyenForm.value.notification_hmac_key,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((adyenForm.value.notification_hmac_key) = $event)),
                    type: _ctx.CompassEnums.InputType.TEXTAREA,
                    error: !!formErrors.value.notification_hmac_key,
                    onFocus: _cache[4] || (_cache[4] = ($event: any) => (formErrors.value.notification_hmac_key = ''))
                  }, null, 8 /* PROPS */, ["modelValue", "type", "error"])
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["label", "error"]),
              _createVNode(_component_CompassFormItem, {
                label: translations.environmentLabel,
                required: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_CompassSelect, {
                    modelValue: adyenForm.value.test_env,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((adyenForm.value.test_env) = $event)),
                    menuClass: "select-adyen-modal"
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(envOptions, (option, index) => {
                        return _createVNode(_component_CompassDropdownOption, {
                          key: index,
                          label: option.label,
                          value: option.value
                        }, null, 8 /* PROPS */, ["label", "value"])
                      }), 64 /* STABLE_FRAGMENT */))
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["modelValue"])
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["label"]),
              (!adyenForm.value.test_env)
                ? (_openBlock(), _createBlock(_component_CompassFormItem, {
                    key: 1,
                    label: translations.apiUrlPrefixLabel,
                    required: "",
                    error: formErrors.value.live_url_prefix
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_CompassInput, {
                        modelValue: adyenForm.value.live_url_prefix,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((adyenForm.value.live_url_prefix) = $event)),
                        error: !!formErrors.value.live_url_prefix,
                        onFocus: _cache[7] || (_cache[7] = ($event: any) => (formErrors.value.live_url_prefix = ''))
                      }, null, 8 /* PROPS */, ["modelValue", "error"])
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["label", "error"]))
                : _createCommentVNode("v-if", true),
              _createVNode(_component_CompassFormItem, {
                label: translations.merchantAccountLabel,
                required: "",
                error: formErrors.value.merchant_account
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_CompassInput, {
                    modelValue: adyenForm.value.merchant_account,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((adyenForm.value.merchant_account) = $event)),
                    error: !!formErrors.value.merchant_account,
                    onFocus: _cache[9] || (_cache[9] = ($event: any) => (formErrors.value.merchant_account = ''))
                  }, null, 8 /* PROPS */, ["modelValue", "error"])
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["label", "error"]),
              _createVNode(_component_CompassFormItem, {
                label: translations.themeIdLabel
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_CompassInput, {
                    modelValue: adyenForm.value.theme_id,
                    "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((adyenForm.value.theme_id) = $event))
                  }, null, 8 /* PROPS */, ["modelValue"])
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["label"]),
              _createVNode(_component_CompassFormItem, {
                label: translations.paymentMethodLabel,
                required: "",
                info: translations.paymentMethodInfo,
                error: formErrors.value.payment_method_id
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_CompassSelect, {
                    modelValue: adyenForm.value.payment_method_id,
                    "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((adyenForm.value.payment_method_id) = $event)),
                    menuClass: "select-adyen-modal"
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(meansOfPaymentOptions.value, (option) => {
                        return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                          key: option.id,
                          label: option.label,
                          value: option.id
                        }, null, 8 /* PROPS */, ["label", "value"]))
                      }), 128 /* KEYED_FRAGMENT */))
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["modelValue"])
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["label", "info", "error"])
            ], 64 /* STABLE_FRAGMENT */))
      ])
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["visible", "size", "primaryAction"]))
}
}

})